import { gql } from "@apollo/client";
import { Alert, ButtonBase, ClickAwayListener, Divider, Fade, Paper, Popper, Skeleton } from "@mui/material";
import { alpha } from "@mui/material";
import DateTime from "atoms/DateTime";
import { PageContentStack } from "atoms/Stack";
import Stack from "atoms/Stack";
import { MAX_CONTENT_WIDTH } from "helpers/breakpoints";
import { COLOR_BLACK, COLOR_GREY_BORDER, COLOR_SECONDARY, COLOR_WHITE_LIGHTER } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING, SPACING_DENSE, SPACING_HALF } from "helpers/spacings";
import useData from "helpers/useData";
import useEnforceAuth from "helpers/useEnforceAuth";
import useRouteState from "helpers/useRouteState";
import useSearchTerm from "helpers/useSearchTerm";
import { Check, Magnify, MenuDown } from "mdi-material-ui";
import React, { useLayoutEffect, useRef, useState } from "react";
import NotFoundPage from "shared/NotFoundPage";
import PageTitle from "shared/PageTitle";

import LeaderboardIntroBanner from "./leaderboard/LeaderboardIntroBanner";
import LeaderboardOverview from "./leaderboard/LeaderboardOverview";
import LeaderboardTableSegment, { LeaderboardTableSegmentFragment } from "./leaderboard/LeaderboardTableSegment";
import TeamPerformance from "./leaderboard/TeamPerformance";
import TeamsComparison from "./leaderboard/TeamsComparison";
import UnifiedLeaderboardTable from "./leaderboard/UnifiedLeaderboardTable";

// The team standing segment is surfaced in its own tab as a full standings table rather than
// inline with the per-game player stat cards.
const TEAM_STANDINGS_TITLE = "Team Standings";

// Sports without per-game player statistics; their player leaderboard tabs are replaced by an info message.
const STATS_UNAVAILABLE_SPORTS = ["pickleball", "netball", "volleyball"];

const dropdownPaperStyle = {
  backgroundColor: COLOR_WHITE_LIGHTER,
  color: COLOR_BLACK,
  border: `solid 1px ${alpha(COLOR_BLACK, 0.15)}`,
  borderRadius: 12,
  boxShadow: `0px 4px 12px ${alpha(COLOR_BLACK, 0.15)}`,
  display: "flex",
  flexFlow: "column nowrap",
  overflow: "hidden",
};

const pickerTriggerStyle = {
  display: "flex",
  flexFlow: "row nowrap",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  border: `1px solid ${alpha(COLOR_BLACK, 0.15)}`,
  borderRadius: 12,
  fontFamily: FONT_FAMILY_GRAPHIK,
  backgroundColor: COLOR_WHITE_LIGHTER,
  textAlign: "left",
};

function SearchField({ searchTerm, searchTermSet, placeholder = "Search..." }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        margin: "12px 16px",
        border: `1px solid ${alpha(COLOR_BLACK, 0.18)}`,
        borderRadius: 20,
        padding: "6px 12px",
        gap: 6,
        backgroundColor: alpha(COLOR_BLACK, 0.03),
      }}
    >
      <Magnify style={{ color: alpha(COLOR_BLACK, 0.4), fontSize: 18, flexShrink: 0 }} />
      <input
        type="search"
        autoFocus
        style={{
          border: "none",
          outline: "none",
          fontFamily: FONT_FAMILY_GRAPHIK,
          fontSize: 14,
          flex: 1,
          backgroundColor: "transparent",
          color: COLOR_BLACK,
          minWidth: 0,
        }}
        value={searchTerm || ""}
        placeholder={placeholder}
        onChange={(e) => searchTermSet(e.target.value)}
      />
    </div>
  );
}

function DropdownItem({ label, sublabel, selected, onClick }) {
  const [hovering, hoveringSet] = useState(false);
  return (
    <ButtonBase
      onMouseEnter={() => hoveringSet(true)}
      onMouseLeave={() => hoveringSet(false)}
      onClick={onClick}
      style={{
        display: "flex",
        flexFlow: "row nowrap",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 16px",
        fontFamily: FONT_FAMILY_GRAPHIK,
        backgroundColor: selected
          ? alpha(COLOR_BLACK, 0.06)
          : hovering
            ? alpha(COLOR_BLACK, 0.03)
            : COLOR_WHITE_LIGHTER,
        color: COLOR_BLACK,
        width: "100%",
        textAlign: "left",
        transition: "background-color 0.15s ease",
        borderRadius: 0,
      }}
    >
      <div style={{ display: "flex", flexFlow: "column nowrap", gap: 6 }}>
        <div style={{ fontWeight: selected ? 600 : 500, fontSize: 13 }}>{label}</div>
        {sublabel && <div style={{ fontSize: 10, color: alpha(COLOR_BLACK, 0.5) }}>{sublabel}</div>}
      </div>
      {selected && <Check style={{ fontSize: 18, color: alpha(COLOR_BLACK, 0.5), flexShrink: 0 }} />}
    </ButtonBase>
  );
}

function TabRow({ tabs, activeKey, onSelect, activeColor = COLOR_BLACK, activeBorderColor = COLOR_BLACK }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {tabs.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onSelect(key)}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeKey === key ? `2px solid ${activeBorderColor}` : "2px solid transparent",
            cursor: "pointer",
            fontFamily: FONT_FAMILY_GRAPHIK,
            fontWeight: activeKey === key ? 600 : 400,
            fontSize: 12,
            color: activeKey === key ? activeColor : alpha(COLOR_BLACK, 0.5),
            padding: `${SPACING_DENSE} ${SPACING_HALF}`,
            transition: "border-color 0.15s, color 0.15s",
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function usePickerDropdown() {
  const ref = useRef(null);
  const [open, openSet] = useState(false);
  const [width, widthSet] = useState(0);
  const toggle = () => {
    widthSet(ref.current?.offsetWidth ?? 0);
    openSet(true);
  };
  return { ref, open, openSet, width, toggle };
}

export default function LeaderboardsPage() {
  useEnforceAuth({ enforceAuthenticated: true });

  // League view state
  const [seasonId, seasonIdSet] = useRouteState("seasonId");
  const [view, viewSet] = useRouteState("view");
  const [tab, tabSet] = useRouteState("tab");
  const [leagueSearchTerm, leagueSearchTermSet, leagueSearchTermDebounced] = useSearchTerm();
  const leaguePicker = usePickerDropdown();

  // Teams view state
  const [teamId, teamIdSet] = useRouteState("teamId");
  const [teamSeasonId, teamSeasonIdSet] = useRouteState("teamSeasonId");
  const [teamsTab, teamsTabSet] = useRouteState("teamsTab");
  const [teamSearchTerm, teamSearchTermSet, teamSearchTermDebounced] = useSearchTerm();
  const teamPicker = usePickerDropdown();
  const teamSeasonPicker = usePickerDropdown();

  const effectiveView = view === "teams" ? "teams" : "league";
  const effectiveTab =
    tab === "allStats"
      ? "allStats"
      : tab === "performance"
        ? "performance"
        : tab === "teamStandings"
          ? "teamStandings"
          : "overview";
  const effectiveTeamsTab = teamsTab === "teamComparison" ? "teamComparison" : "leaderboard";

  const [data] = useData(
    gql`
      query LeaderboardsPage(
        $seasonId: ID
        $leagueSearchTerm: String!
        $teamId: ID
        $teamSeasonId: ID
        $teamSearchTerm: String
      ) {
        season(id: $seasonId) {
          id
          nameWithLeague
          firstPlayedAt
          lastPlayedAt
          leaderboardTableSegments {
            title
            ...LeaderboardTableSegmentFragment
          }
          league {
            id
            name
            sport
          }
        }
        teamData: team(id: $teamId) {
          id
          name
          seasons {
            id
            nameWithLeague
            firstPlayedAt
            lastPlayedAt
          }
          leaderboardTableSegments(seasonId: $teamSeasonId) {
            title
            ...LeaderboardTableSegmentFragment
          }
          performanceSnapshotRows(seasonId: $teamSeasonId) {
            title
            avgCell {
              value
              gamesCount
              people {
                id
                fullNameWithAnonymisation
              }
            }
            totalCell {
              value
              people {
                id
                fullNameWithAnonymisation
              }
            }
            highCell {
              value
              people {
                id
                fullNameWithAnonymisation
              }
            }
          }
        }
        teamSeason: season(id: $teamSeasonId) {
          id
          leaderboardTableSegments {
            title
            ...LeaderboardTableSegmentFragment
          }
        }
        currentUser {
          id
          gamesSeasons(searchTerm: $leagueSearchTerm, limit: 20) {
            id
            nameWithLeague
            firstPlayedAt
            lastPlayedAt
          }
          gamesTeams(searchTerm: $teamSearchTerm) {
            id
            name
          }
        }
      }
      ${LeaderboardTableSegmentFragment}
    `,
    {
      seasonId,
      leagueSearchTerm: leagueSearchTermDebounced,
      teamId,
      teamSeasonId,
      teamSearchTerm: teamSearchTermDebounced,
    },
  );

  // Auto-select first team when entering teams view
  const firstTeamId = data?.currentUser?.gamesTeams?.[0]?.id;
  useLayoutEffect(() => {
    if (effectiveView === "teams" && !teamId && firstTeamId) {
      teamIdSet(firstTeamId);
    }
  }, [effectiveView, teamId, firstTeamId]);

  useLayoutEffect(() => {
    leagueSearchTermSet("");
  }, [seasonId]);

  useLayoutEffect(() => {
    teamSearchTermSet("");
  }, [teamId]);

  if (data && !data.season) return <NotFoundPage />;

  // Sports that don't track per-game player statistics — the player leaderboard tabs are hidden for these.
  const statsUnavailable = STATS_UNAVAILABLE_SPORTS.includes(data?.season?.league?.sport);
  const leagueSegments = data?.season?.leaderboardTableSegments ?? [];
  const hasLeagueSegments = leagueSegments.length > 0;
  // Team standings (shown when leaderboard_team_standing_visible is set on the league/competition)
  // get their own tab + full standings table; the rest are per-game player stat segments.
  const teamStandingSegment = leagueSegments.find((segment) => segment.title === TEAM_STANDINGS_TITLE);
  const leaguePlayerSegments = leagueSegments.filter((segment) => segment.title !== TEAM_STANDINGS_TITLE);
  const hasLeaguePlayerSegments = leaguePlayerSegments.length > 0;
  const teamData = data?.teamData;
  const teamSegments = teamData?.leaderboardTableSegments ?? [];
  const hasTeamSegments = teamSegments.length > 0;
  const performanceRows = teamData?.performanceSnapshotRows ?? [];
  const teamSeasonSegments = data?.teamSeason?.leaderboardTableSegments ?? [];
  const teamSeasons = teamData?.seasons ?? [];

  const selectedTeamSeason = teamSeasons.find((s) => s.id === teamSeasonId);

  return (
    <>
      <PageTitle title="Leaderboards" />
      <LeaderboardIntroBanner data={data} teamData={teamData} effectiveView={effectiveView} viewSet={viewSet} />

      {/* Sticky selector + tabs bar */}
      <div
        style={{
          backgroundColor: COLOR_WHITE_LIGHTER,
          borderBottom: `1px solid ${COLOR_GREY_BORDER}`,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <div
          style={{
            margin: "0 auto",
            maxWidth: MAX_CONTENT_WIDTH,
            width: `calc(100vw - ${SPACING} * 2)`,
            padding: `${SPACING_HALF} 0 0`,
            display: "flex",
            flexFlow: "column nowrap",
            gap: SPACING_HALF,
          }}
        >
          {effectiveView === "league" && (
            <>
              {/* League season picker */}
              <ButtonBase
                data-test-leaderboard-season-picker
                ref={leaguePicker.ref}
                onClick={leaguePicker.toggle}
                style={{ ...pickerTriggerStyle, width: 360 }}
              >
                <div style={{ display: "flex", flexFlow: "column nowrap", gap: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: COLOR_BLACK }}>
                    {data?.season?.nameWithLeague ?? <Skeleton width={200} />}
                  </div>
                  {data?.season && (
                    <div style={{ fontSize: 10, color: alpha(COLOR_BLACK, 0.5) }}>
                      <DateTime value={data.season.firstPlayedAt} variant="month" />
                      {" - "}
                      <DateTime value={data.season.lastPlayedAt} variant="month" />
                    </div>
                  )}
                </div>
                <MenuDown
                  style={{
                    color: alpha(COLOR_BLACK, 0.5),
                    transform: leaguePicker.open ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease-out",
                  }}
                />
              </ButtonBase>

              <Popper
                anchorEl={leaguePicker.ref.current}
                open={leaguePicker.open}
                placement="bottom-start"
                transition
                style={{ zIndex: 20, width: leaguePicker.width || "auto" }}
              >
                {({ TransitionProps }) => (
                  <Fade {...TransitionProps} timeout={200}>
                    <div>
                      <ClickAwayListener onClickAway={() => leaguePicker.openSet(false)}>
                        <Paper square style={dropdownPaperStyle} data-test-leaderboard-season-dropdown>
                          <SearchField searchTerm={leagueSearchTerm} searchTermSet={leagueSearchTermSet} />
                          <Divider />
                          {data?.currentUser?.gamesSeasons.map((season, index) => (
                            <React.Fragment key={season.id}>
                              {index > 0 && <Divider />}
                              <DropdownItem
                                label={season.nameWithLeague}
                                sublabel={
                                  <>
                                    <DateTime value={season.firstPlayedAt} variant="month" />
                                    {" - "}
                                    <DateTime value={season.lastPlayedAt} variant="month" />
                                  </>
                                }
                                selected={season.id === seasonId}
                                onClick={() => {
                                  seasonIdSet(season.id);
                                  leaguePicker.openSet(false);
                                }}
                              />
                            </React.Fragment>
                          ))}
                        </Paper>
                      </ClickAwayListener>
                    </div>
                  </Fade>
                )}
              </Popper>

              <TabRow
                tabs={[
                  { key: "overview", label: "Overview" },
                  { key: "allStats", label: "All Stats" },
                  ...(teamStandingSegment ? [{ key: "teamStandings", label: "Team Standings" }] : []),
                ]}
                activeKey={effectiveTab}
                onSelect={(key) => tabSet(key === "overview" ? null : key)}
              />
            </>
          )}

          {effectiveView === "teams" && (
            <>
              {/* Team + season pickers side by side */}
              <div style={{ display: "flex", flexFlow: "row wrap", gap: SPACING_HALF }}>
                {/* Team picker */}
                <ButtonBase
                  ref={teamPicker.ref}
                  onClick={teamPicker.toggle}
                  style={{ ...pickerTriggerStyle, flex: "1 1 200px", minWidth: 180, maxWidth: 320 }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: COLOR_BLACK,
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {teamData?.name ??
                      (teamId ? <Skeleton width={160} /> : data ? "Select team" : <Skeleton width={160} />)}
                  </div>
                  <MenuDown
                    style={{
                      color: alpha(COLOR_BLACK, 0.5),
                      flexShrink: 0,
                      transform: teamPicker.open ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease-out",
                    }}
                  />
                </ButtonBase>

                {/* Team season picker */}
                <ButtonBase
                  ref={teamSeasonPicker.ref}
                  onClick={teamSeasonPicker.toggle}
                  style={{ ...pickerTriggerStyle, flex: "1 1 160px", minWidth: 140, maxWidth: 240 }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: COLOR_BLACK,
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selectedTeamSeason?.nameWithLeague ?? "All Seasons"}
                  </div>
                  <MenuDown
                    style={{
                      color: alpha(COLOR_BLACK, 0.5),
                      flexShrink: 0,
                      transform: teamSeasonPicker.open ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease-out",
                    }}
                  />
                </ButtonBase>
              </div>

              {/* Team picker popper */}
              <Popper
                anchorEl={teamPicker.ref.current}
                open={teamPicker.open}
                placement="bottom-start"
                transition
                style={{ zIndex: 20, width: teamPicker.width || "auto", minWidth: 260 }}
              >
                {({ TransitionProps }) => (
                  <Fade {...TransitionProps} timeout={200}>
                    <div>
                      <ClickAwayListener onClickAway={() => teamPicker.openSet(false)}>
                        <Paper square style={dropdownPaperStyle}>
                          <SearchField
                            searchTerm={teamSearchTerm}
                            searchTermSet={teamSearchTermSet}
                            placeholder="Search teams..."
                          />
                          <Divider />
                          {data?.currentUser?.gamesTeams?.map((team, index) => (
                            <React.Fragment key={team.id}>
                              {index > 0 && <Divider />}
                              <DropdownItem
                                label={team.name}
                                selected={team.id === teamId}
                                onClick={() => {
                                  teamIdSet(team.id);
                                  teamPicker.openSet(false);
                                }}
                              />
                            </React.Fragment>
                          ))}
                        </Paper>
                      </ClickAwayListener>
                    </div>
                  </Fade>
                )}
              </Popper>

              {/* Team season picker popper */}
              <Popper
                anchorEl={teamSeasonPicker.ref.current}
                open={teamSeasonPicker.open}
                placement="bottom-start"
                transition
                style={{ zIndex: 20, width: teamSeasonPicker.width || "auto", minWidth: 200 }}
              >
                {({ TransitionProps }) => (
                  <Fade {...TransitionProps} timeout={200}>
                    <div>
                      <ClickAwayListener onClickAway={() => teamSeasonPicker.openSet(false)}>
                        <Paper square style={dropdownPaperStyle}>
                          <DropdownItem
                            label="All Seasons"
                            selected={!teamSeasonId}
                            onClick={() => {
                              teamSeasonIdSet(null);
                              teamSeasonPicker.openSet(false);
                            }}
                          />
                          {teamSeasons.map((season) => (
                            <React.Fragment key={season.id}>
                              <Divider />
                              <DropdownItem
                                label={season.nameWithLeague}
                                sublabel={
                                  <>
                                    <DateTime value={season.firstPlayedAt} variant="month" />
                                    {" - "}
                                    <DateTime value={season.lastPlayedAt} variant="month" />
                                  </>
                                }
                                selected={season.id === teamSeasonId}
                                onClick={() => {
                                  teamSeasonIdSet(season.id);
                                  teamSeasonPicker.openSet(false);
                                }}
                              />
                            </React.Fragment>
                          ))}
                        </Paper>
                      </ClickAwayListener>
                    </div>
                  </Fade>
                )}
              </Popper>

              {/* Primary tabs: Leaderboard / Team Comparison */}
              <TabRow
                tabs={[
                  { key: "leaderboard", label: "Team Leaderboard" },
                  { key: "teamComparison", label: "Team Comparison" },
                ]}
                activeKey={effectiveTeamsTab}
                onSelect={(key) => teamsTabSet(key === "leaderboard" ? null : key)}
              />

              {/* Contextual sub-tabs for the Leaderboard view, nested below the primary tabs */}
              {effectiveTeamsTab === "leaderboard" && (
                <TabRow
                  tabs={[
                    { key: "overview", label: "Overview" },
                    { key: "allStats", label: "All Stats" },
                    { key: "performance", label: "Performance" },
                  ]}
                  activeKey={effectiveTab}
                  onSelect={(key) => tabSet(key === "overview" ? null : key)}
                  activeColor={COLOR_SECONDARY}
                  activeBorderColor={COLOR_SECONDARY}
                />
              )}
            </>
          )}
        </div>
      </div>

      <PageContentStack>
        <Stack
          gap={SPACING}
          style={{ margin: "0 auto", maxWidth: MAX_CONTENT_WIDTH, width: `calc(100vw - ${SPACING} * 2)` }}
        >
          {/* League view */}
          {effectiveView === "league" && (
            <>
              {!data && <Skeleton variant="rectangular" height={200} />}
              {data && statsUnavailable && effectiveTab !== "teamStandings" && (
                <Alert variant="outlined" severity="info" icon={false}>
                  <p>Leaderboard statistics are not available for this sport at this time.</p>
                </Alert>
              )}
              {data && !statsUnavailable && !hasLeagueSegments && (
                <Alert variant="outlined" severity="info" icon={false}>
                  <p>Leaderboards are not yet available. Change filter to view other leaderboards.</p>
                </Alert>
              )}
              {data && !statsUnavailable && hasLeaguePlayerSegments && effectiveTab === "overview" && (
                <LeaderboardOverview segments={leaguePlayerSegments} />
              )}
              {data && !statsUnavailable && hasLeaguePlayerSegments && effectiveTab === "allStats" && (
                <UnifiedLeaderboardTable segments={leaguePlayerSegments} title="All Stats" />
              )}
              {data && teamStandingSegment && effectiveTab === "teamStandings" && (
                <LeaderboardTableSegment leaderboardTableSegment={teamStandingSegment} collapsible />
              )}
            </>
          )}

          {/* Teams view */}
          {effectiveView === "teams" && (
            <>
              {!data && <Skeleton variant="rectangular" height={200} />}
              {data && effectiveTeamsTab === "leaderboard" && !hasTeamSegments && (
                <Alert variant="outlined" severity="info" icon={false}>
                  <p>No leaderboard data available for this team.</p>
                </Alert>
              )}
              {data && effectiveTeamsTab === "leaderboard" && hasTeamSegments && effectiveTab === "overview" && (
                <LeaderboardOverview segments={teamSegments} />
              )}
              {data && effectiveTeamsTab === "leaderboard" && hasTeamSegments && effectiveTab === "allStats" && (
                <UnifiedLeaderboardTable segments={teamSegments} title="All Stats" />
              )}
              {data && effectiveTeamsTab === "leaderboard" && effectiveTab === "performance" && (
                <TeamPerformance rows={performanceRows} />
              )}
              {data && effectiveTeamsTab === "teamComparison" && !teamSeasonId && (
                <Alert variant="outlined" severity="info" icon={false}>
                  <p>Select a season above to compare teams.</p>
                </Alert>
              )}
              {data && effectiveTeamsTab === "teamComparison" && teamSeasonId && (
                <TeamsComparison segments={teamSeasonSegments} selectedTeamId={teamData?.id} />
              )}
            </>
          )}
        </Stack>
      </PageContentStack>
    </>
  );
}
